import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@localagent/ui';

export interface ContextMenuItem {
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  destructive?: boolean;
  onSelect: () => void;
}

export interface ContextMenuProps {
  /** Viewport coordinates, from e.g. a contextmenu event's clientX/clientY. */
  x: number;
  y: number;
  open: boolean;
  onClose: () => void;
  items: ContextMenuItem[];
  label?: string;
  className?: string;
}

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function ContextMenu({ x, y, open, onClose, items, label, className }: ContextMenuProps) {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const enabledIndexes = React.useMemo(
    () => items.map((item, i) => (item.disabled ? null : i)).filter((i): i is number => i !== null),
    [items],
  );

  React.useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const width = contentRef.current?.getBoundingClientRect().width ?? 192;
    const height = contentRef.current?.getBoundingClientRect().height ?? 0;
    const margin = 8;
    const top = Math.max(margin, Math.min(y, window.innerHeight - height - margin));
    const left = Math.max(margin, Math.min(x, window.innerWidth - width - margin));
    setActiveIndex(enabledIndexes[0] ?? 0);
    setPosition({ top, left });
  }, [open, x, y, enabledIndexes]);

  React.useLayoutEffect(() => {
    if (open && position !== null) contentRef.current?.focus();
  }, [open, position]);

  React.useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Tab') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter' && e.key !== ' ') {
        return;
      }
      e.preventDefault();
      if (enabledIndexes.length === 0) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const current = enabledIndexes.indexOf(activeIndex);
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        const next =
          enabledIndexes[
            (current + delta + enabledIndexes.length) % enabledIndexes.length
          ];
        setActiveIndex(next);
        return;
      }
      const item = items[activeIndex];
      if (item && !item.disabled) {
        item.onSelect();
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, items, activeIndex, enabledIndexes]);

  if (!open) return null;

  const content = (
    <div
      ref={contentRef}
      role="menu"
      aria-label={label}
      tabIndex={-1}
      data-testid="context-menu"
      className={cn(
        // Overlay border is the ADR-002-amendment exception: a floating menu
        // sits over arbitrary content and has no layout region to separate.
        // Items themselves stay borderless — hover/active is a fill.
        'z-50 fixed min-w-[12rem] rounded-md border bg-popover p-2 text-popover-foreground shadow-md focus:outline-none',
        className,
      )}
      style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' }}
    >
      {items.map((item, i) => (
        <button
          key={item.label}
          type="button"
          role="menuitem"
          disabled={item.disabled}
          aria-disabled={item.disabled || undefined}
          data-testid={`context-menu-item-${slugify(item.label)}`}
          onClick={() => {
            if (item.disabled) return;
            item.onSelect();
            onClose();
          }}
          onMouseMove={() => {
            if (!item.disabled) setActiveIndex(i);
          }}
          className={cn(
            'flex w-full items-center gap-2 rounded px-3 py-2 text-left text-body',
            item.destructive ? 'text-destructive hover:bg-destructive/10' : 'hover:bg-accent',
            !item.disabled && activeIndex === i && (item.destructive ? 'bg-destructive/10' : 'bg-accent'),
            item.disabled && 'cursor-not-allowed disabled:opacity-50',
            !item.disabled && 'focus-visible:ring-2 ring-ring',
          )}
        >
          {item.icon && <span className="size-4 shrink-0">{item.icon}</span>}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );

  return createPortal(content, document.body);
}
